import get from 'lodash/get';
import classnames from 'classnames';
import React, { useEffect, useMemo, useReducer, useState } from 'react';
import pathfinder from '@fiverr-private/pathfinder';
import { I18n, translate } from '@fiverr-private/i18n-react';
import { getContext } from '@fiverr-private/fiverr_context';
import { breakpointReverseSm } from '@fiverr-private/sass/helpers';
import { LogoProjectListings, updateQueryParams, listings, TARGET_TYPES } from '@fiverr-private/variations_display';
import { makersExperience } from '@fiverr-private/logo_maker_analytics';
import { triggerSignUp } from '@fiverr-private/identification_triggers';
import monitor, { EVENT_TYPES, STATIC_IMPRESSION_ENRICHMENT, TRACKING_PAGE_NAMES } from '../../../service/bigquery';
import metrics from '../../../service/metrics';
import * as userSavedProjects from '../../../shared/api/userSavedProjects';
import { useUserAgentContext } from '../../../shared/components/UserAgentContext';
import { useEffectOnce, useOnceBeforeRender, useTopLevelSource } from '../../../shared/hooks';
import { deserializeBriefData } from '../../../shared/lib';
import { reportAddToFavoritesClick, reportCreateNewLogoClick } from '../../../shared/reportEvents/bigQuery';
import getAlgoAbTestsConfig from '../../../shared/server/experiments/algo';
import { raiseToast, TOAST_TYPES } from '../../../shared/utils/raiseToast';
import { useMatchMedia } from '../../../shared/hooks/useMatchMedia';
import useOnHeaderNewLogoLinkClick from '../../../shared/hooks/useOnHeaderNewLogoLinkClick';
import { AVATAR_PROPS } from '../../../shared/reportEvents/bigQuery/reportCreateNewLogoClick/constants';
import { ROUTES as BRIEF_STEPS } from '../Brief/constants';
import { LISTINGS as LISTINGS_PROPTYPES } from './propTypes/listings';
import { getBriefTrackingData } from './lib';
import {
    GAMING_INDUSTRIES,
    LISTING_ROUTE_SOURCE,
    LISTINGS_IMPRESSION_ENRICHMENT,
    LISTINGS_PAGE_BODY_CLASS,
    MAX_LOAD_MORE_ALLOWED,
    PAGE_ELEMENT_TEXT_TYPES,
    USER_SAVING_ACTIONS,
} from './constants';
import RefineResults from './RefineResults';
import TextFields from './TextFields';
import GamingBanner from './GamingBanner';
import FavoritesEducation, { hasUserFavoritesEducation, setUserFavoritesEducation } from './FavoritesEducation';
import './style.scss';

const {
    setData,
    reportAddToFavorites,
    reportRemoveFromFavorites,
    reportEditBriefClick,
    reportHoverLogo,
    reportHoverLogoVariation,
    reportLogoCardArrowClick,
    reportLogoCardClick,
    reportListingTextFieldChange,
    reportViewMoreVariationsClicked,
    reportListingView,
} = makersExperience.buyer;

const getTextDefaults = () => ({
    brandName: translate('logo_maker_perseus.general.brand_name_placeholder'),
});

const getInitialState = ({ briefData, impressionEnrichment, queryBrandName, querySlogan }) => {
    const textDefaults = getTextDefaults();

    const { brand_name: brandName = textDefaults.brandName, slogan = textDefaults.slogan } = get(
        briefData,
        'logo_name',
        {}
    );

    return {
        briefData,
        slogan: querySlogan || slogan,
        brandName: queryBrandName || brandName,
        impressionEnrichment,
    };
};

const getImpressionEnrichment = (baseImpressionEnrichment = {}) => {
    const { pageCtxId, refCtxId, ...restOfBaseEnrichment } = baseImpressionEnrichment;
    const { page: pageEnrichment, ...restOfEnrichment } = LISTINGS_IMPRESSION_ENRICHMENT;

    return {
        ...restOfBaseEnrichment,
        ...restOfEnrichment,
        page: {
            ...pageEnrichment,
            referrer: { ctx_id: refCtxId },
        },
        gig_impression: [
            {
                listings: {
                    page_ctx_id: pageCtxId,
                },
            },
        ],
    };
};

/**
 * @param {String} type, field type.
 * @param {String} label, current field name (will depend on the locale).
 */
const getPageElementEnrichment = ({ type, label }) => ({
    page: {
        element: {
            name: PAGE_ELEMENT_TEXT_TYPES[type],
            text: label,
        },
    },
});

const getLogoProjectCardEnrichment = ({ projectId, variationId }) => ({
    makers_project: {
        id: projectId,
        variation: {
            id: variationId,
        },
    },
});

/**
 * Returns project by card details
 * @param cardDetails
 * @param projects
 * @returns project
 */
const getProject = (cardDetails, projects) => {
    const { projectId } = cardDetails;
    return projects.find(({ id }) => projectId === id);
};

const getVariationPreviewImpressionEnrichment = (cardDetails, sellerId, enrichment, projects) => {
    const { briefId, gig_impression, ...restOfEnrichment } = enrichment;
    const project = getProject(cardDetails, projects);
    const { alg, ...rest } = gig_impression[0];
    const impression = {
        ...rest,
        alg: {
            ...alg,
            name: project.algoPool,
        },
        type: project.engineType,
    };

    return {
        makers_project: {
            id: cardDetails.projectId,
            brief: { id: briefId },
            variation: { id: cardDetails.variationId },
        },
        seller: { id: sellerId },
        gig_impression: impression,
        item_context: {
            position: cardDetails.logoPosition, // position of the logo in the listing
            variation_position: cardDetails.variationPosition, // position of the variation
        },
        ...restOfEnrichment,
        ...STATIC_IMPRESSION_ENRICHMENT,
    };
};

const Listings = ({
    briefId,
    appData: {
        listingsProjects,
        statistics,
        userSavedProjects: initialUserSavedProjects = [],
        briefData: initialBriefData,
        impressionEnrichment: initialImpressionEnrichment,
        hasNextPage: initialHasNextPage,
    },
    currentUser,
    requireRegistration,
    totalPages,
    tracking,
}) => {
    const { queryParameters, abTests } = getContext();
    const algoAbTestsConfig = getAlgoAbTestsConfig(abTests);
    const { brand_name: queryBrandName, slogan: querySlogan } = queryParameters;
    const { isTouch, isMobile, isTablet } = useUserAgentContext();

    const { brandName, slogan, briefData, ...initialState } = getInitialState({
        briefData: initialBriefData,
        impressionEnrichment: initialImpressionEnrichment,
        queryBrandName,
        querySlogan,
    });

    const [projects, setProjects] = useState(listingsProjects);
    const [baseImpressionEnrichment, setBaseImpressionEnrichment] = useState(initialState.impressionEnrichment);
    const [textFields, setTextFields] = useState({ brandName, slogan });
    const [page, setPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(initialHasNextPage);
    const [loadMoreCount, setLoadMoreCount] = useState(0);
    const [isMobileView, setIsMobileView] = useState(isMobile);
    const [showEducation, setShowEducation] = useState(false);
    const gamingBannerShouldBeDisplayed = useMemo(
        () =>
            briefData &&
            briefData.industries &&
            briefData.industries.some((industry) => GAMING_INDUSTRIES.includes(industry)),
        [briefData]
    );
    const [userSavings, dispatchUserSavings] = useReducer((state, { item, type }) => {
        switch (type) {
            case USER_SAVING_ACTIONS.ADD:
                return [...state, item];
            case USER_SAVING_ACTIONS.REMOVE:
                return state.filter(({ project_id }) => project_id !== item.project_id);
            default:
                return state;
        }
    }, initialUserSavedProjects);

    useOnHeaderNewLogoLinkClick(() => {
        reportCreateNewLogoClick({
            pageName: TRACKING_PAGE_NAMES.BUYER_LISTINGS,
            ...AVATAR_PROPS,
        });
    });

    const handleProjectSaving = () => {
        if (hasUserFavoritesEducation(currentUser.id)) {
            return;
        }
        if (!isTouch) {
            setShowEducation(true);
        }
    };

    const closeEducation = () => {
        setUserFavoritesEducation(currentUser.id);
        setShowEducation(false);
    };

    // Need to have a map to control saving flow and to not trigger re-rendering
    const [savingStatuses] = useState(new Map());

    const { refCtxId, uid } = tracking;
    const impressionEnrichment = getImpressionEnrichment({
        ...baseImpressionEnrichment,
        refCtxId,
        uid,
    });

    useTopLevelSource(queryParameters.source);

    useOnceBeforeRender(() =>
        setData({
            brief: { ...deserializeBriefData(briefData), id: briefId },
            statistics,
        })
    );

    // reset document.body.style.height to auto to make sure scroll listeners will work
    useEffect(() => {
        if (document) {
            document.body.style.height = 'auto';
        }
    }, []);

    // append special class to the body to enable specific global styling on this page
    useEffect(() => {
        document.body.classList.add(LISTINGS_PAGE_BODY_CLASS);
        return () => {
            document.body.classList.remove(LISTINGS_PAGE_BODY_CLASS);
        };
    }, []);

    useMatchMedia(`(max-width: ${breakpointReverseSm}px)`, ({ matches }) => {
        setIsMobileView(matches);
    });

    useEffectOnce(() => {
        monitor.trackPageView({ route: LISTING_ROUTE_SOURCE });
        reportListingView();
    });

    const createUserSaving = async (projectId, variationId) => {
        const projectIndex = projects.findIndex(({ id }) => id === projectId);
        const project = projects[projectIndex];
        if (!project) {
            return;
        }
        const variationPosition = project.variations.findIndex(({ id }) => id === variationId) + 1;
        reportAddToFavorites({
            sellerId: project.user_id,
            projectId,
            variationId,
            logoPosition: projectIndex + 1,
            variationPosition,
        });

        handleProjectSaving();
        const data = {
            projectId,
            variationId,
            briefId,
            ...textFields,
        };

        const id = await userSavedProjects.create(data);
        dispatchUserSavings({
            type: USER_SAVING_ACTIONS.ADD,
            item: {
                id,
                project_id: projectId,
                variation_id: variationId,
                brand_name: textFields.brandName,
                slogan: textFields.slogan,
            },
        });
    };

    const removeUserSaving = (projectId) => {
        const userSavingsToRemove = userSavings.find(({ project_id }) => project_id === projectId);
        if (!userSavingsToRemove) {
            return;
        }
        reportRemoveFromFavorites();

        dispatchUserSavings({
            type: USER_SAVING_ACTIONS.REMOVE,
            item: userSavingsToRemove,
        });
        const { id: userSavedId } = userSavingsToRemove;
        return userSavedProjects.delete(userSavedId);
    };

    const changeUserSaving = async (projectId, variationId, status) => {
        if (savingStatuses.get(projectId)) {
            return false;
        }

        savingStatuses.set(projectId, true);

        status ? await createUserSaving(projectId, variationId) : await removeUserSaving(projectId);

        const { pageCtxId } = baseImpressionEnrichment;
        reportAddToFavoritesClick({
            projectId,
            variationId,
            pageCtxId,
            type: status ? USER_SAVING_ACTIONS.ADD : USER_SAVING_ACTIONS.REMOVE,
            pageName: TRACKING_PAGE_NAMES.BUYER_LISTINGS,
        });

        savingStatuses.set(projectId, false);
        return true;
    };

    const loadMore = async () => {
        try {
            const {
                projects: additionalProjects,
                impressionEnrichment,
                hasNextPage,
            } = await listings.get({
                briefId,
                page,
                briefData,
                algoAbTestsConfig,
                totalPages,
            });

            setPage(page + 1);
            setBaseImpressionEnrichment(impressionEnrichment);
            setProjects([...projects, ...additionalProjects]);

            setLoadMoreCount(loadMoreCount + 1);
            setHasNextPage(hasNextPage);
        } catch {
            setHasNextPage(false);
            raiseToast({
                type: TOAST_TYPES.ERROR,
                content: <I18n k="logo_maker_perseus.general.something_went_wrong" />,
            });
        }
    };

    const handleLogoProjectCardClick = (cardDetails) => {
        const project = getProject(cardDetails, projects);

        monitor.track(EVENT_TYPES.LISTINGS_VARIATION_CARD_CLICKED, {
            ...getLogoProjectCardEnrichment(cardDetails),
            source: LISTING_ROUTE_SOURCE,
        });
        reportLogoCardClick({ ...cardDetails, ...project });
    };

    const handleLogoCardVariationPreviewClick = (cardDetails) => {
        const project = getProject(cardDetails, projects);

        monitor.track(EVENT_TYPES.LISTINGS_CLICKED_ON_VARIATION_PREVIEW, {
            ...getLogoProjectCardEnrichment(cardDetails),
            source: LISTING_ROUTE_SOURCE,
            gig_impression: [{ type: project.engineType }],
            item_context: {
                position: cardDetails.logoPosition, // position of the logo in the listing
                variation_position: cardDetails.variationPosition, // position of the variation
            },
        });
        reportLogoCardArrowClick({ ...cardDetails, ...project });
    };

    const handleLogoCardVariationPreviewHover = (cardDetails) => {
        if (!isTouch) {
            const sellerId = projects.find(({ id }) => id === cardDetails.projectId)?.user_id;

            monitor.track(EVENT_TYPES.LISTINGS_HOVERED_ON_VARIATION_PREVIEW, {
                ...getVariationPreviewImpressionEnrichment(
                    cardDetails,
                    sellerId,
                    { ...impressionEnrichment, uid },
                    projects
                ),
                url: window.location.href,
            });

            reportHoverLogoVariation(cardDetails);
        }
    };

    const handleTextFieldsChange = ({ brandName, slogan }, textFieldsMetadata = []) => {
        setTextFields({ brandName, slogan });

        // Store brand name and slogan in the query params
        updateQueryParams({
            brand_name: brandName,
            slogan,
        });

        reportListingTextFieldChange({ brandName, slogan });

        const briefTrackingData = getBriefTrackingData(briefId, {
            ...briefData,
            logo_name: {
                brand_name: brandName,
                slogan,
            },
        });

        Array.isArray(textFieldsMetadata)
            ? monitor.multitrack(
                  textFieldsMetadata.map((metadata) => ({
                      type: EVENT_TYPES.LISTINGS_BRIEF_METADATA_UPDATED,
                      payload: {
                          ...briefTrackingData,
                          ...getPageElementEnrichment(metadata),
                      },
                  }))
              )
            : monitor.track(EVENT_TYPES.LISTINGS_BRIEF_METADATA_UPDATED, {
                  ...briefTrackingData,
                  ...getPageElementEnrichment(textFieldsMetadata),
              });
    };

    const handleEditBriefClick = () => {
        monitor.track(EVENT_TYPES.LISTINGS_REFINE_BRIEF_CLICKED, {
            makers_project: {
                brief: { id: briefId },
            },
        });
        reportEditBriefClick();

        window.location = pathfinder(
            'logo_maker_brief_page_logo_maker_brief_step_name',
            { step_name: BRIEF_STEPS.BRIEF_LOGO_NAME },
            { query: { brief_id: briefId } }
        );
    };

    const isLoadMoreAllowed = loadMoreCount < MAX_LOAD_MORE_ALLOWED && hasNextPage;
    const showRefineResults = !isLoadMoreAllowed;
    const handleLoadMore = isLoadMoreAllowed ? loadMore : undefined;

    const onPreviewLoad = ({ loadTime }) => metrics.time('buyer.listing.preview.download.time', loadTime);

    useEffect(() => {
        if (!currentUser && requireRegistration) {
            const signUpEventEnrichment = monitor.enrich(EVENT_TYPES.LISTINGS_REGISTRATION_POPUP);
            triggerSignUp({
                modalOptions: {
                    isSticky: true,
                    redirectUrl: window.location.href,
                    triggerType: 'system_trigger',
                },
                biEnrichmentParams: signUpEventEnrichment,
            }).catch(() => {
                window.location.assign(pathfinder('login'));
            });
        }
    }, [currentUser, requireRegistration]);

    const logoProjectListingsEventHandlers = {
        onLogoProjectCardClick: handleLogoProjectCardClick,
        onLogoProjectArrowClick: handleLogoCardVariationPreviewClick,
        onLogoProjectHover: (params) => {
            if (isTouch) {
                return;
            }

            reportHoverLogo(params);
        },
        onLogoProjectVariationHover: handleLogoCardVariationPreviewHover,
        onLoadMoreVariationsClick: reportViewMoreVariationsClicked,
    };

    return (
        <div
            className={classnames('makers-buyer-experience', {
                'shown-education': showEducation,
            })}
        >
            {showEducation && <FavoritesEducation onClose={closeEducation} />}
            <div className="app-wrapper">
                <div className="buyer-listings">
                    <header>
                        <h3>
                            <I18n k={'logo_maker_perseus.buyer.listing.buyer.title'} />
                        </h3>
                        <span>
                            <I18n k={'logo_maker_perseus.buyer.listing.buyer.subtitle'} />
                        </span>
                    </header>
                    <TextFields
                        {...textFields}
                        onChange={handleTextFieldsChange}
                        isMobile={isMobile}
                        isTouch={isTouch}
                        onEditBriefClick={handleEditBriefClick}
                    />
                    {projects && projects.length && (
                        <LogoProjectListings
                            projects={projects}
                            loadMore={handleLoadMore}
                            impressionEnrichment={impressionEnrichment}
                            navigationParams={{
                                brief_id: briefId,
                                ref_ctx_id: refCtxId,
                                source: LISTING_ROUTE_SOURCE,
                            }}
                            onPreviewLoad={onPreviewLoad}
                            userSavings={userSavings}
                            showUserSavings={true}
                            onUserSavingsChange={changeUserSaving}
                            {...textFields}
                            {...logoProjectListingsEventHandlers}
                            isMobile={isMobile}
                            isTablet={isTablet}
                            target={isTouch ? TARGET_TYPES.SELF : TARGET_TYPES.BLANK}
                            banner={gamingBannerShouldBeDisplayed ? <GamingBanner /> : null}
                            bannerRow={isMobileView ? 11 : 4}
                        />
                    )}
                    {showRefineResults && <RefineResults briefId={briefId} onEditBrief={handleEditBriefClick} />}
                </div>
            </div>
        </div>
    );
};

Listings.propTypes = LISTINGS_PROPTYPES;

export default Listings;
